import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useLoad, useProjectEvents } from '../../lib/hooks';
import type { Comment, Person, Project } from '../../lib/types';
import { useAuth } from '../../state/auth';
import { Empty, ErrorState, Spinner } from '../../components/ui';
import { CommentInput } from '../../review/CommentInput';
import { Thread } from '../../review/Thread';
import { useComments } from '../../review/useComments';

/** Feedback that is not about one spot: navigation, sitemap, copy direction, requests. */
export function DiscussionTab({ project, canComment, canManage }: { project: Project; canComment: boolean; canManage: boolean }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [showResolved, setShowResolved] = useState(false);
  const { comments, loading, error, reload, actions } = useComments(project.id, { general: true });
  const people = useLoad(() => api.get<{ people: Person[] }>(`/api/projects/${project.id}/people`), [project.id]).data?.people ?? [];
  useProjectEvents(project.id, (event) => {
    if (event.type.startsWith('comment.') || event.type === 'resync') void reload(true);
  });

  const selectedId = params.get('comment');
  const select = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('comment', id);
    else next.delete('comment');
    setParams(next, { replace: true });
    if (id) actions.markRead(id);
  };

  const visible = comments.filter((c) => showResolved || c.status !== 'resolved' || c.id === selectedId);
  const resolvedCount = comments.filter((c) => c.status === 'resolved').length;
  const linkFor = (comment: Comment) => `${location.origin}/projects/${project.id}?tab=discussion&comment=${comment.id}`;

  return (
    <div className="stack" style={{ gap: 16, maxWidth: 720 }}>
      <p className="muted">For feedback that is not about one spot on a page: navigation, sitemap and structure, copy direction, content you still need to send.</p>
      {canComment && (
        <CommentInput
          projectId={project.id}
          people={people}
          placeholder="Write a note for the team…"
          submitLabel="Post"
          showCategory
          onSubmit={async (draft) => {
            await actions.create(draft);
          }}
        />
      )}
      {loading && comments.length === 0 ? (
        <Spinner label="Loading discussion" />
      ) : error && comments.length === 0 ? (
        <ErrorState title="The discussion could not be loaded" message={error} onRetry={() => void reload()} />
      ) : visible.length === 0 ? (
        <Empty icon="message" title={comments.length ? 'Everything here is resolved' : 'No general feedback yet'}>
          {canComment ? 'Pinned revisions live inside each review item. Use this space for everything else.' : 'General notes from the team and client will show up here.'}
        </Empty>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          {visible.map((comment) => (
            <Thread
              key={comment.id}
              comment={comment}
              projectId={project.id}
              people={people}
              selected={comment.id === selectedId}
              canComment={canComment}
              canManage={canManage}
              currentUserId={user?.id ?? null}
              actions={actions}
              linkFor={linkFor}
              onSelect={() => select(comment.id === selectedId ? null : comment.id)}
            />
          ))}
        </div>
      )}
      {resolvedCount > 0 && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setShowResolved((value) => !value)}>
          {showResolved ? 'Hide' : 'Show'} {resolvedCount} resolved
        </button>
      )}
    </div>
  );
}
